<template>
  <teleport to="body">
    <div v-if="modelValue" class="info-overlay" @keydown.esc="close">
      <div class="info-modal" role="dialog" :aria-label="title">
        <header class="info-header">
          <h3>{{ title }}</h3>
          <button class="info-close" @click="close">✕</button>
        </header>
        <section class="info-body">
          <template v-if="fields.length">
            <form @submit.prevent="confirmAndClose">
              <div class="form-group">
                <div v-for="field in fields" :key="field.key" class="input-field">
                  <label :for="field.key">{{ field.label }}</label>
                  <input :id="field.key" v-model="values[field.key]" :type="field.type || 'text'" />
                </div>
              </div>
            </form>
          </template>
          <template v-else>
            <slot />
          </template>
        </section>
        <footer class="info-footer">
          <button class="info-exit info-cancel" @click="cancelAndClose">Cancel</button>
          <button class="info-exit info-confirm" @click="confirmAndClose">Confirm</button>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script>
import { defineComponent, reactive, watch } from 'vue';

export default defineComponent({
  name: 'InputPopup',
  props: {
    modelValue: { type: Boolean, required: true },
    title: { type: String, default: 'Info' },
    fields: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'confirm', 'cancel'],
  setup(props, { emit }) {
    const values = reactive({});
    watch(
      () => props.fields,
      (fields) => {
        Object.keys(values).forEach(k => delete values[k]);
        fields.forEach(f => {
          values[f.key] = f.default ?? '';
        });
      },
      { immediate: true }
    );

    function close() {
      emit('update:modelValue', false);
    }
    function confirmAndClose() {
      emit('confirm', { ...values });
      emit('update:modelValue', false);
    }
    function cancelAndClose() {
      emit('cancel');
      emit('update:modelValue', false);
    }
    return { close, confirmAndClose, cancelAndClose, values };
  }
});
</script>

<style scoped>
.info-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.info-modal {
  background: #fff;
  width: 500px;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.info-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
}

.info-body {
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
}

.info-footer {
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: flex-end;
}

.info-exit {
  margin-left: 20px;
  padding: 6px 14px;
  background: var(--background-white-hover);
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: background .2s;
}

.info-exit:hover {
  background: var(--background-white-hover-darker);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.input-field {
  display: flex;
  flex-direction: column;
}

.input-field label {
  margin-bottom: 0.25rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-color, #333);
}

.input-field input,
.input-field textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color, #ccc);
  border-radius: 4px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.input-field textarea {
  resize: vertical;
  min-height: 80px;
}

.info-confirm {
  background-color: var(--purple);
  color: white;
  font-weight: 500;
}

.info-confirm:hover {
  background-color: var(--purple-hover);
  color: white;
}
</style>
